import React, { FC, useContext, useEffect, useMemo, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import "./upsUpdatePaymentMethod.scss";
import {
  UPSPaymentFailedCard,
  UPSUpdatePaymentMethodContainer,
} from "@app/ups/components";
import { PaymentPlanStatus } from "@app/ups/models";
import { UPSAPIContext } from "@app/provider";
import { routes } from "@app/routing";
import { useNavigate } from "react-router-dom";
import {
  parsePaymentMethodSummary,
  PaymentMethodSummaryObject,
} from "@app/ups/utils";
import { logMsg } from "@app/utils/logMsg";

const messages = defineMessages({
  upsUpdatePaymentMethodTitle: {
    id: "upsUpdatePaymentMethodTitle",
    defaultMessage: "Update your payment method",
  },
  upsUpdatePaymentMethodSubtitle: {
    id: "upsUpdatePaymentMethodSubtitle",
    defaultMessage: "You can use credit, debit, FSA, or HSA card",
  },
});

interface FailedCardInfo extends PaymentMethodSummaryObject {
  installmentCount: number;
}

const UPSUpdatePaymentMethod: FC = () => {
  const intl = useIntl();
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [customerSessionSecret, setCustomerSessionSecret] = useState("");
  const [failedCardInfo, setFailedCardInfo] = useState<FailedCardInfo>();
  const navigate = useNavigate();

  const {
    upsApiProviderState: { guestValidationData },
    getPaymentPlanRequest,
    createPaymentMethod,
  } = useContext(UPSAPIContext);

  const paymentPlanId = useMemo(
    () => guestValidationData?.paymentPlanId,
    [guestValidationData?.paymentPlanId]
  );

  const handlePaymentSetup = async () => {
    try {
      setLoading(true);
      if (!paymentPlanId) {
        navigate(routes.upsGuestLogin);
        return;
      }

      const planResponse = await getPaymentPlanRequest(paymentPlanId);
      const planData = planResponse?.data;

      if (
        planData?.status === PaymentPlanStatus.ERROR ||
        planData?.status === PaymentPlanStatus.ACTIVE
      ) {
        if (planData.status === PaymentPlanStatus.ERROR) {
          const parsed = parsePaymentMethodSummary(
            planData?.paymentMethodSummary
          );
          if (parsed) {
            setFailedCardInfo({
              ...parsed,
              installmentCount: planData?.numberOfInstallments,
            });
          }
        }

        const paymentResponse = await createPaymentMethod({
          patientUid: planData.patientUid,
        });

        setClientSecret(paymentResponse.data?.clientSecret as string);
        setCustomerSessionSecret(
          paymentResponse.data?.customerSessionSecret as string
        );
      } else {
        navigate(routes.upsNoLongerAvailable);
      }
    } catch {
      logMsg("error", "ups.payment", "Payment setup failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handlePaymentSetup();
  }, [guestValidationData?.paymentPlanId]);

  return (
    <div className="ups-update-payment-method">
      {failedCardInfo && <UPSPaymentFailedCard {...failedCardInfo} />}

      <h1 className="ups-update-payment-method__title">
        {intl.formatMessage(messages.upsUpdatePaymentMethodTitle)}
      </h1>
      <div className="ups-update-payment-method__subtitle">
        {intl.formatMessage(messages.upsUpdatePaymentMethodSubtitle)}
      </div>

      <UPSUpdatePaymentMethodContainer
        clientSecret={clientSecret}
        customerSessionSecret={customerSessionSecret}
        loading={loading}
      />
    </div>
  );
};

export default UPSUpdatePaymentMethod;
